/**
 * Indian Rupee (INR) display helpers — single place for price/currency formatting.
 * Uses locale `en-IN` for grouping and symbols.
 */

export interface FormatInrRupeeOptions {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  /**
   * When value is null, undefined, empty string, or NaN.
   * - `dash` → '-'
   * - `zero` → '₹ 0.00' or '₹0.00' depending on {@link symbolSpacing}
   */
  whenNull?: 'dash' | 'zero';
  /**
   * `true` (default) → `₹ 1,234.56`
   * `false` → `₹1,234.56`
   */
  symbolSpacing?: boolean;
}

export interface FormatInrNumberOptions {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

/** API / UI fields that may be number, string, empty, or absent before INR parsing. */
export type InrParseableValue = number | string | null | undefined;

/** Parse API numeric fields that may be string or number. */
export function parseInrValue(value: InrParseableValue): number | null {
  if (value === null || value === undefined || value === '') return null;
  const num = typeof value === 'string' ? Number.parseFloat(value) : value;
  if (Number.isNaN(num)) return null;
  return num;
}

/**
 * Full currency string with ₹ and Indian grouping.
 * Default: null/NaN → '-', spaced symbol `₹ 1,234`.
 */
export function formatInrRupee(value: InrParseableValue, options?: FormatInrRupeeOptions): string {
  const whenNull = options?.whenNull ?? 'dash';
  const symbolSpacing = options?.symbolSpacing ?? true;
  const parsed = parseInrValue(value);
  if (parsed === null) {
    if (whenNull === 'dash') return '-';
    const min = options?.minimumFractionDigits ?? 2;
    const max = options?.maximumFractionDigits ?? 2;
    const zeroStr = formatInrDigits(0, { minimumFractionDigits: min, maximumFractionDigits: max });
    return symbolSpacing ? `₹ ${zeroStr}` : `₹${zeroStr}`;
  }
  const formatOpts: FormatInrNumberOptions = {};
  if (options?.minimumFractionDigits !== undefined) {
    formatOpts.minimumFractionDigits = options.minimumFractionDigits;
  }
  if (options?.maximumFractionDigits !== undefined) {
    formatOpts.maximumFractionDigits = options.maximumFractionDigits;
  }
  const numStr = formatInrDigits(parsed, formatOpts);
  return symbolSpacing ? `₹ ${numStr}` : `₹${numStr}`;
}

/**
 * Indian grouping only (no ₹). Use when composing with a custom currency prefix, e.g. summary cards.
 */
export function formatInrDigits(value: number, options?: FormatInrNumberOptions): string {
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: options?.minimumFractionDigits,
    maximumFractionDigits: options?.maximumFractionDigits,
  }).format(value);
}

/**
 * Formatted amount with ₹ and `en-IN` grouping (default spacing `₹ 1,234.56`).
 */
export function formatInrNumber(value: number, options?: FormatInrNumberOptions): string {
  return `₹ ${formatInrDigits(value, options)}`;
}

/**
 * Plain amount with ₹ for simple concatenation contexts (2 decimals, '-' if invalid).
 */
export function formatInrPlainAmount(value: number | string): string {
  const numValue = typeof value === 'string' ? Number.parseFloat(value) : value;
  if (Number.isNaN(numValue)) return '-';
  return formatInrNumber(numValue, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Indian-formatted amount with ₹, or '-' when null/invalid.
 */
export function formatInrNumberOrDash(
  value: InrParseableValue,
  options?: FormatInrNumberOptions
): string {
  const parsed = parseInrValue(value);
  if (parsed === null) return '-';
  return formatInrNumber(parsed, options);
}
